/**
 * Sharp Badge Tooltip Text
 *
 * Centralized text generation for all 7 sharp money badge explanations.
 * Used by both web (LegCard.tsx) and mobile (ParlayCard.tsx) to ensure
 * identical contextual explanations across platforms.
 */

import type { LegLineShoppingSignals, LegSharpSignals } from './sharpMoney.ts';

/** Context about the leg needed to generate contextual text */
export interface BadgeTextContext {
  playerName: string;
  statLabel: string;
  /** "Over" | "Under" | null */
  overUnder: string | null;
  line: number | null;
  signals: LegSharpSignals;
}

/** Result: title + body text for a badge tooltip */
export interface BadgeText {
  title: string;
  body: string;
}

// =============================================================================
// Public API
// =============================================================================

export function getConsensusText(ctx: BadgeTextContext): BadgeText {
  const { playerName, statLabel, signals, line } = ctx;
  const direction = ctx.overUnder?.toLowerCase();
  const isOver = direction === 'over';
  const isUnder = direction === 'under';

  let body = `Pinnacle${signals.pinnacleLine != null ? ` (${signals.pinnacleLine})` : ''} and ProphetX${signals.prophetxLine != null ? ` (${signals.prophetxLine})` : ''} both set ${playerName}'s ${statLabel} line within 0.5 pts of each other. `;

  if (direction && signals.sharpLine != null && line != null) {
    if ((isOver && signals.sharpLine > line) || (isUnder && signals.sharpLine < line)) {
      body += `Both sharp books support your ${ctx.overUnder} — they see the line ${isOver ? 'higher' : 'lower'} than your book.`;
    } else if ((isOver && signals.sharpLine < line) || (isUnder && signals.sharpLine > line)) {
      body += `Both sharp books disagree with your ${ctx.overUnder} — they see the line on the other side. Proceed with caution.`;
    } else {
      body += `Both sharp books agree, and their line matches your book (${line}) — the market has priced this accurately. No clear edge in either direction, but the consensus adds confidence in the number.`;
    }
  } else {
    body += 'Two independent sharp books agreeing adds confidence in this number.';
  }

  return { title: 'Sharp Consensus', body };
}

export function getSharpLineText(ctx: BadgeTextContext): BadgeText {
  const { playerName, statLabel, signals, line } = ctx;
  const direction = ctx.overUnder?.toLowerCase();
  const isOver = direction === 'over';
  const isUnder = direction === 'under';
  const bookName =
    signals.sharpBookSource === 'pinnacle'
      ? 'Pinnacle'
      : signals.sharpBookSource === 'prophetx'
        ? 'ProphetX'
        : 'Pinnacle';
  const gap =
    line != null && signals.sharpLine != null
      ? Math.abs(signals.sharpLine - line).toFixed(1)
      : null;
  const sharpHigher = line != null && signals.sharpLine != null && signals.sharpLine > line;
  const sharpLower = line != null && signals.sharpLine != null && signals.sharpLine < line;

  let body =
    signals.sharpBookSource === 'both'
      ? `Pinnacle (${signals.pinnacleLine}) and ProphetX (${signals.prophetxLine}) disagree. Using Pinnacle as the reference — it's the sharper book. `
      : `${bookName} has ${playerName}'s ${statLabel} at ${signals.sharpLine}. `;

  if (gap && sharpHigher && direction) {
    body += `That's ${gap} pts higher than your book's line (${line}). Sharp money sees a higher number — ${isOver ? 'supports your Over' : 'challenges your Under'}.`;
  } else if (gap && sharpLower && direction) {
    body += `That's ${gap} pts lower than your book's line (${line}). Sharp money sees a lower number — ${isUnder ? 'supports your Under' : 'challenges your Over'}.`;
  } else if (gap && (sharpHigher || sharpLower) && !direction) {
    body += `That's ${gap} pts ${sharpHigher ? 'higher' : 'lower'} than your book's line (${line}).`;
  } else if (line != null) {
    body += `That matches your book's line (${line}) — no edge either way.`;
  }

  return { title: 'Sharp Line', body };
}

export function getPositiveEVText(ctx: BadgeTextContext): BadgeText {
  const { playerName, statLabel, signals, line } = ctx;
  const direction = ctx.overUnder?.toLowerCase();

  let body =
    signals.evPercent != null
      ? `Your book's line on ${playerName}'s ${statLabel} is better than the sharp market price by ${signals.evPercent.toFixed(1)}%. `
      : `Your book's line on ${playerName}'s ${statLabel} is better than the sharp market price. `;

  if (direction) {
    body += `Your ${ctx.overUnder}${line != null ? ` ${line}` : ''} has a mathematical edge — the sharp books would set this less favorably. This gap tends to close as books adjust, so the value is time-sensitive.`;
  } else {
    body +=
      "You're getting a better price than professional bettors. The edge may close as lines converge.";
  }

  if (signals.sharpBookSource === 'both' && signals.sharpConsensus) {
    body += ' Both Pinnacle and ProphetX confirm this edge.';
  }

  return { title: 'Positive Expected Value', body };
}

export function getRLMText(ctx: BadgeTextContext): BadgeText {
  const { playerName, statLabel, signals } = ctx;
  const direction = ctx.overUnder?.toLowerCase();
  const isOver = direction === 'over';
  const isUnder = direction === 'under';

  let body: string;

  if (signals.rlmIsDataDriven) {
    // Pinnacle-confirmed: soft book line is diverging away from Pinnacle (gap widening).
    // Much stronger signal — Pinnacle is the sharpest book in the world.
    //
    // Determine sharp side from the Pinnacle gap, NOT from line movement direction.
    // If Pinnacle line > soft book line, sharps see the number higher → sharps favor Over.
    // If Pinnacle line < soft book line, sharps see the number lower → sharps favor Under.
    const sharpsFavorOver =
      signals.sharpLine != null && ctx.line != null && signals.sharpLine > ctx.line;
    const sharpsFavorUnder =
      signals.sharpLine != null && ctx.line != null && signals.sharpLine < ctx.line;
    const sharpsAgreeWithPick = (isOver && sharpsFavorOver) || (isUnder && sharpsFavorUnder);
    const sharpsDisagreeWithPick = (isOver && sharpsFavorUnder) || (isUnder && sharpsFavorOver);

    const gapStr =
      signals.sharpLine != null && ctx.line != null
        ? ` (${Math.abs(signals.sharpLine - ctx.line).toFixed(1)} pt gap vs Pinnacle)`
        : '';
    body = `${playerName}'s ${statLabel} line is moving away from Pinnacle's reference line${gapStr}. Pinnacle attracts the world's sharpest bettors — when a soft book diverges from Pinnacle and keeps moving further away, it typically signals sharp money is one-sided. `;

    if (sharpsAgreeWithPick) {
      body += `The divergence favors your ${ctx.overUnder} — strong confirmation from the sharpest market in the world.`;
    } else if (sharpsDisagreeWithPick) {
      body += `The divergence is against your ${ctx.overUnder}. Sharp market pressure opposes this pick — treat this as a caution flag.`;
    } else if (direction) {
      body += `The divergence is against your ${ctx.overUnder}. Sharp market pressure opposes this pick — treat this as a caution flag.`;
    } else {
      body += 'This is a data-driven signal from Pinnacle market divergence.';
    }
  } else {
    // Heuristic fallback: no Pinnacle data. Uses "public bets overs → line moves up" assumption.
    const lineWentDown = (signals.lineMovement ?? 0) < 0;
    const heuristicSharpsAgree = (isOver && !lineWentDown) || (isUnder && lineWentDown);
    body = `${playerName}'s ${statLabel} line moved ${lineWentDown ? 'down' : 'up'} against typical public betting patterns. When a line moves against the crowd, it usually means professional bettors are wagering heavily enough to overpower public money. (Note: detected via line movement pattern — no Pinnacle data available to confirm.) `;

    if (heuristicSharpsAgree) {
      body += `The professional money appears to be on the same side as your ${ctx.overUnder}.`;
    } else if (direction) {
      body += `The professional money appears to be on the opposite side of your ${ctx.overUnder}. Consider this a caution flag — though the movement does give your bet a more favorable number.`;
    } else {
      body += 'This is an indicator of potential professional betting action on this prop.';
    }
  }

  return { title: 'Reverse Line Movement', body };
}

export function getLineMovementText(ctx: BadgeTextContext): BadgeText {
  const { playerName, statLabel, signals } = ctx;
  const direction = ctx.overUnder?.toLowerCase();
  const isOver = direction === 'over';
  const isUnder = direction === 'under';
  const moved = signals.lineMovement ?? 0;
  const magnitude = Math.abs(moved).toFixed(1);
  const wentDown = moved < 0;
  const wentUp = moved > 0;

  let body = `${playerName}'s ${statLabel} line has ${wentDown ? 'dropped' : 'risen'} ${magnitude} pts since opening. `;

  if (isOver && wentDown) {
    body += `The market lowered expectations for ${playerName}, suggesting less confidence in high output. On the other hand, you're clearing a lower bar — your Over is easier to hit than it would have been at the opening line.`;
  } else if (isOver && wentUp) {
    body += `The market expects more ${statLabel} from ${playerName}, which agrees with your Over direction. However, you're betting at a higher number than was available at open — the easier Over window may have passed.`;
  } else if (isUnder && wentDown) {
    body += `The market agrees with your Under direction — expectations for ${playerName} have come down. However, the line dropped with it, so your Under needs ${playerName} to stay below a tighter number than was available at open.`;
  } else if (isUnder && wentUp) {
    body += `The market raised expectations for ${playerName}, which goes against your Under. On the other hand, the higher line gives your Under more room — ${playerName} has a bigger cushion before going over.`;
  } else {
    body += `The line shifted ${magnitude} pts since opening.`;
  }

  return { title: 'Line Movement', body };
}

export function getMiddleText(ctx: BadgeTextContext, shopping?: LegLineShoppingSignals): BadgeText {
  const { playerName, statLabel } = ctx;
  let body = `Two books have different lines on ${playerName}'s ${statLabel}. If you bet the Over at one book and Under at the other, the result could land between both lines — winning both bets.`;

  if (shopping?.middleBooks) {
    const mb = shopping.middleBooks;
    body += ` Over ${mb.overLine} at ${mb.overBook} · Under ${mb.underLine} at ${mb.underBook}`;
    if (shopping.middleSize != null) {
      body += ` (${shopping.middleSize.toFixed(1)} pt gap)`;
    }
    body += '.';

    // Winning range — what integer/half-point values win both sides
    const winLow = Math.ceil(mb.overLine);
    const winHigh = Math.floor(mb.underLine);
    if (winLow <= winHigh) {
      body +=
        winLow === winHigh
          ? ` Result needs to land on exactly ${winLow} to win both.`
          : ` Result needs to land between ${winLow}–${winHigh} to win both.`;
    }

    // Profit estimate using real decimal odds
    if (mb.overOdds > 0 && mb.underOdds > 0) {
      // Bet 1 unit each side = 2 units wagered
      const bothHitReturn = mb.overOdds + mb.underOdds;
      const bothHitProfit = (((bothHitReturn - 2) / 2) * 100).toFixed(1);
      const bestOdds = Math.max(mb.overOdds, mb.underOdds);
      const oneMissLoss = (((2 - bestOdds) / 2) * 100).toFixed(1);
      body += ` If both hit: ~${bothHitProfit}% profit · If one misses: ~${oneMissLoss}% loss.`;
    }
  }

  body +=
    ' This is a rare hedged play — you only profit if the result lands in the gap, but your downside is limited when the gap between books is large enough.';
  return { title: 'Middle Opportunity', body };
}

export function getArbitrageText(
  ctx: BadgeTextContext,
  shopping?: LegLineShoppingSignals,
): BadgeText {
  const { playerName, statLabel } = ctx;
  let body = `The odds on ${playerName}'s ${statLabel} are misaligned across books — betting both Over and Under at different books guarantees profit regardless of outcome.`;

  if (shopping?.arbitrageProfitPercent != null) {
    body += ` Guaranteed ${shopping.arbitrageProfitPercent.toFixed(1)}% profit on total wagered.`;
  }

  if (shopping?.arbBooks) {
    const ab = shopping.arbBooks;
    body += ` Over @ ${ab.overBook}`;
    if (ab.overOdds > 0) body += ` (${ab.overOdds.toFixed(2)})`;
    body += ` · Under @ ${ab.underBook}`;
    if (ab.underOdds > 0) body += ` (${ab.underOdds.toFixed(2)})`;
    body += '.';
  }

  body +=
    ' These windows are rare and close quickly (minutes). Act fast or treat it as a signal that books disagree on this line.';
  return { title: 'Arbitrage Opportunity', body };
}

// =============================================================================
// Push Notification Text (concise, ~100 chars for push notifications)
// =============================================================================

/** Concise line movement notification for push. Uses mixed-signal framing. */
export function getLineMovementNotificationBody(ctx: BadgeTextContext): string {
  const { playerName, statLabel, signals, line } = ctx;
  const direction = ctx.overUnder?.toLowerCase();
  const moved = signals.lineMovement ?? 0;
  const magnitude = Math.abs(moved).toFixed(1);
  const wentUp = moved > 0;
  // currentLine = user's tracked line + movement delta (movement is currentMarketLine - userLine)
  const currentLine =
    line != null && signals.lineMovement != null ? +(line + signals.lineMovement).toFixed(1) : null;

  const lineRange = line != null && currentLine != null ? ` (${line} → ${currentLine})` : '';
  const dirText = wentUp ? 'rose' : 'dropped';

  if (direction === 'over' && wentUp) {
    return `${playerName} ${statLabel} ${dirText} ${magnitude}${lineRange}. Market agrees with your Over, but tougher number now.`;
  } else if (direction === 'over' && !wentUp) {
    return `${playerName} ${statLabel} ${dirText} ${magnitude}${lineRange}. Easier number for your Over, but market moved against.`;
  } else if (direction === 'under' && !wentUp) {
    return `${playerName} ${statLabel} ${dirText} ${magnitude}${lineRange}. Market agrees with your Under, but tighter target.`;
  } else if (direction === 'under' && wentUp) {
    return `${playerName} ${statLabel} ${dirText} ${magnitude}${lineRange}. Market moved against your Under, but higher line gives more room.`;
  }

  return `${playerName} ${statLabel} ${dirText} ${magnitude}${lineRange}.`;
}

/**
 * Concise sharp signal notification for push.
 * Returns null when signal data is insufficient (e.g., sharpLine is null for 'sharp_line').
 */
export function getSharpSignalNotificationBody(
  ctx: BadgeTextContext,
  signalType: 'rlm' | 'positive_ev' | 'consensus' | 'sharp_line',
): string | null {
  const { playerName, statLabel, signals, line } = ctx;
  const direction = ctx.overUnder?.toLowerCase();
  const dirLabel = ctx.overUnder ?? '';

  switch (signalType) {
    case 'positive_ev': {
      const evStr = signals.evPercent != null ? `${signals.evPercent.toFixed(1)}% edge on ` : '';
      return `+EV detected: ${evStr}${playerName} ${statLabel} ${dirLabel}${line != null ? ` ${line}` : ''}. Your book's price is better than sharp market.`;
    }
    case 'rlm': {
      // Determine sharp side from Pinnacle gap when available, fall back to line movement direction
      let sharpSide: string;
      if (signals.sharpLine != null && line != null && signals.sharpLine !== line) {
        sharpSide = signals.sharpLine > line ? 'Over' : 'Under';
      } else {
        const lineWentDown = (signals.lineMovement ?? 0) < 0;
        sharpSide = lineWentDown ? 'Under' : 'Over';
      }
      // Include line numbers when available for actionable push notifications
      let lineInfo = '';
      if (line != null && signals.lineMovement != null) {
        const openingLine = Number((line - signals.lineMovement).toFixed(1));
        const sign = signals.lineMovement > 0 ? '+' : '';
        lineInfo = `: ${openingLine} → ${line} (${sign}${signals.lineMovement.toFixed(1)})`;
      }
      return `Reverse line movement on ${playerName} ${statLabel}${lineInfo}. Sharp money appears to favor ${sharpSide}${direction ? ` — ${direction === sharpSide.toLowerCase() ? 'supports' : 'opposes'} your ${ctx.overUnder}` : ''}.`;
    }
    case 'consensus': {
      const pinLine = signals.pinnacleLine != null ? ` (${signals.pinnacleLine})` : '';
      const propLine = signals.prophetxLine != null ? ` (${signals.prophetxLine})` : '';
      let tail = '';
      if (direction && signals.sharpLine != null && line != null) {
        const isOver = direction === 'over';
        if ((isOver && signals.sharpLine > line) || (!isOver && signals.sharpLine < line)) {
          tail = ` Both support your ${ctx.overUnder}.`;
        } else if ((isOver && signals.sharpLine < line) || (!isOver && signals.sharpLine > line)) {
          tail = ` Both disagree with your ${ctx.overUnder}.`;
        }
      }
      return `Sharp consensus: Pinnacle${pinLine} and ProphetX${propLine} agree on ${playerName} ${statLabel}.${tail}`;
    }
    case 'sharp_line': {
      if (signals.sharpLine == null) return null;
      const gap = line != null ? Math.abs(signals.sharpLine - line).toFixed(1) : null;
      const higher = line != null && signals.sharpLine > line;
      return `Sharp line on ${playerName} ${statLabel}: ${signals.sharpLine}${gap ? ` (${gap} pts ${higher ? 'higher' : 'lower'} than your ${line})` : ''}.`;
    }
  }
}

// =============================================================================
// Slate-Wide Notification Text (market intelligence framing, no "your leg")
// =============================================================================

/** Slate injury notification body. Market intelligence framing. */
export function getSlateInjuryNotificationBody(
  playerName: string,
  status: 'Out' | 'Out For Season' | 'Doubtful',
  gameLabel: string,
): string {
  switch (status) {
    case 'Out':
      return `${playerName} (starter) is OUT for ${gameLabel} tonight.`;
    case 'Out For Season':
      return `${playerName} (starter) is OUT FOR SEASON — ${gameLabel}.`;
    case 'Doubtful':
      return `${playerName} (starter) is Doubtful for ${gameLabel} tonight. Monitor status.`;
  }
}

/** A big line move at one book on tonight's slate. */
export interface SlateMarketMove {
  playerName: string;
  statLabel: string;
  bookName: string;
  matchup: string;
  openingLine: number;
  currentLine: number;
}

/** What the sharp books say about a slate move. No pick direction: the slate has none. */
export interface SlateSharpRead {
  isReverseLineMovement: boolean;
  sharpConsensus: boolean;
  /** Pinnacle's line when it has the prop, else ProphetX's. */
  sharpLine: number | null;
  pinnacleLine: number | null;
  prophetxLine: number | null;
}

/**
 * Slate move notification body, one message per prop per book. Sharp signals are appended
 * to the move rather than sent separately.
 */
export function getSlateMarketMoveNotificationBody(
  move: SlateMarketMove,
  sharp?: SlateSharpRead,
): string {
  const direction = move.currentLine > move.openingLine ? 'rose' : 'dropped';
  const parts = [
    `${move.playerName} ${move.statLabel} ${direction} ${move.openingLine} → ${move.currentLine} at ${move.bookName} (${move.matchup}).`,
  ];
  if (!sharp) return parts[0];

  const side =
    sharp.sharpLine == null || sharp.sharpLine === move.currentLine
      ? null
      : sharp.sharpLine > move.currentLine
        ? 'Over'
        : 'Under';
  if (sharp.isReverseLineMovement) {
    const sharpBook =
      sharp.pinnacleLine != null
        ? ` (Pinnacle ${sharp.pinnacleLine})`
        : sharp.prophetxLine != null
          ? ` (ProphetX ${sharp.prophetxLine})`
          : '';
    parts.push(
      side
        ? `Reverse line movement: sharp books favor the ${side}${sharpBook}.`
        : 'Reverse line movement on this prop.',
    );
  }
  if (sharp.sharpConsensus && sharp.pinnacleLine != null && sharp.prophetxLine != null) {
    parts.push(
      `Pinnacle ${sharp.pinnacleLine} and ProphetX ${sharp.prophetxLine} agree${side ? ` on the ${side}` : ''}.`,
    );
  }
  return parts.join(' ');
}

/** Concise injury notification for push. */
export function getInjuryNotificationBody(
  playerName: string,
  status: 'Out' | 'Out For Season' | 'Questionable' | 'Doubtful',
  opponentTeam: string | null,
  prop: string | null,
  isGameLevelLeg: boolean,
): string {
  const gameStr = opponentTeam ? ` vs ${opponentTeam}` : '';

  if (isGameLevelLeg) {
    return `${playerName} (starter) is ${status}${gameStr}. This could affect your ${prop ?? 'bet'}.`;
  }

  switch (status) {
    case 'Out':
      return `${playerName} is OUT${gameStr}. Your ${prop ?? 'leg'} is at risk.`;
    case 'Out For Season':
      return `${playerName} is OUT FOR SEASON${gameStr}. Your ${prop ?? 'leg'} is at risk.`;
    case 'Doubtful':
      return `${playerName} is Doubtful${gameStr}. High chance of missing — monitor closely.`;
    case 'Questionable':
      return `${playerName} is now Questionable${gameStr}. Monitor status before tip-off.`;
  }
}
